import React, { useMemo } from "react";
import { Button, Modal, SelectionCard, Typography } from "../../../common";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import { BarChart, PieChart } from "../../../common/charts";
import { DataVisualizationContent } from "../../../../types/newtypes";
import { useDataVisualizationPreview } from "./hooks/useDataVisualizationPreview";
import { FeedbackMessage } from "../ui-helpers";

interface DataVisualizationPreviewProps {
  content: DataVisualizationContent;
  onCompleted?: () => void;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

const DataVisualizationPreview: React.FC<DataVisualizationPreviewProps> = ({
  content,
  onCompleted,
  onCardStateChange,
  cardId,
}) => {
  // Normalize options to support both `options` and legacy `answer_options`
  const normalizedOptions = useMemo(() => {
    const coerce = (o: any) => {
      // Accept strings or objects with various keys
      if (typeof o === 'string') return { text: o };
      const text = o?.text ?? o?.label ?? o?.answer ?? '';
      const remediation = o?.remediation
        ? o.remediation
        : (o?.feedback_text ? { type: 'text', content: o.feedback_text } : undefined);
      // Preserve raw correctness value (string/number/bool) for robust checks downstream
      let rawCorrect = (o?.is_correct ?? o?.isCorrect ?? o?.correct);
      if (rawCorrect === undefined || rawCorrect === null) {
        const ft = (o?.feedback_text || '').toString().trim().toLowerCase();
        if (ft.startsWith('correct')) rawCorrect = true;
      }
      const derivedBoolean = (() => {
        if (typeof rawCorrect === 'boolean') return rawCorrect;
        if (typeof rawCorrect === 'string') return rawCorrect.toLowerCase() === 'true' || rawCorrect === '1';
        if (typeof rawCorrect === 'number') return rawCorrect === 1;
        return false;
      })();

      return {
        text,
        is_correct: typeof rawCorrect === 'undefined' ? derivedBoolean : rawCorrect,
        remediation,
        feedback_text: o?.feedback_text,
        __isCorrect: derivedBoolean,
        __rawCorrect: rawCorrect,
      } as any;
    };

    const opts = (content as any)?.options;
    if (Array.isArray(opts) && opts.length > 0) return opts.map(coerce);
    const legacy = (content as any)?.answer_options;
    if (Array.isArray(legacy)) return legacy.map(coerce);
    return [] as any[];
  }, [content]);

  // Provide a content object for the hook with normalized options
  const hookContent = useMemo(() => ({
    ...(content as any),
    options: normalizedOptions,
  }), [content, normalizedOptions]);

  const {
    selectedAnswer,
    isSelectionCorrect,
    feedbackTitle,
    feedbackMessage,
    handleOptionSelect,
    handleResetAnswers,
    isWhyModalOpen,
    getCorrectAnswerExplanation,
    setIsWhyModalOpen,
  } = useDataVisualizationPreview({
    content: hookContent as any,
    onCardStateChange,
    cardId,
  });

  const hasAnswered = isSelectionCorrect !== null;

  // Normalize table fields to support both
  // - column_headers/table_data (new spec)
  // - headers/rows (legacy transform from production backend)
  const effectiveColumnHeaders: string[] = useMemo(() => {
    return (
      content?.column_headers || ((content as any)?.headers as string[]) || []
    );
  }, [content]);

  const effectiveTableData: Array<Record<string, string | number>> = useMemo(() => {
    const rows = (content?.table_data || (content as any)?.rows) as
      | Array<Record<string, string | number>>
      | string[][]
      | undefined;
    if (!rows) return [];

    // If rows are arrays (string[][]), map to objects using headers
    if (Array.isArray(rows) && Array.isArray(rows[0])) {
      const arrayRows = rows as unknown as string[][];
      return arrayRows.map((arr) => {
        const obj: Record<string, string | number> = {};
        effectiveColumnHeaders.forEach((h, i) => {
          obj[h] = arr[i];
        });
        return obj;
      });
    }

    return rows as Array<Record<string, string | number>>;
  }, [content, effectiveColumnHeaders]);

  return (
    <div className="w-full">
      <Typography variant="h4" weight="bold" className="mb-4">
        {content?.slide_title}
      </Typography>

      <Typography variant="p" className="!text-base mb-6">
        {content?.intro_text}
      </Typography>

      <div className="bg-white rounded-2xl border border-gray-200 mb-6">
        <div className="p-6">
          {content?.chart_type === "bar" || content?.chart_type === "bar chart" ? (
            content?.data && <BarChart data={content.data} />
          ) : content?.chart_type === "table" ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <tr>
                    {effectiveColumnHeaders?.map((header, index) => (
                      <th
                        key={index}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b border-gray-200"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {effectiveTableData?.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      {effectiveColumnHeaders?.map((header) => (
                        <td
                          key={header}
                          className="px-6 py-4 text-sm text-gray-900"
                        >
                          {row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            content?.data && <PieChart data={content.data} />
          )}
        </div>
      </div>

      <div className="mb-4 mt-4">
        <MarkdownWrapper content={content?.question || ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {normalizedOptions?.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const showCorrect =
            hasAnswered && option?.is_correct && isSelectionCorrect === true;
          const showIncorrect =
            hasAnswered && isSelectionCorrect === false && isSelected;

          return (
            <SelectionCard
              key={index}
              type="default"
              label={option?.text}
              checked={isSelected}
              correct={showCorrect}
              incorrect={showIncorrect}
              disabled={hasAnswered}
              onClick={() => handleOptionSelect(index)}
            />
          );
        })}
      </div>

      {hasAnswered && (
        <FeedbackMessage
          isSuccess={isSelectionCorrect === true}
          title={feedbackTitle}
          message={feedbackMessage}
          showTryAgain={isSelectionCorrect === false}
          onTryAgain={handleResetAnswers}
        />
      )}

      <Modal
        activeModal={isWhyModalOpen}
        onClose={() => setIsWhyModalOpen(false)}
        title={"Why 🧐"}
        footerContent={
          <Button
            label="Close"
            variant="filled"
            size="large"
            onClick={() => setIsWhyModalOpen(false)}
          />
        }
      >
        <Typography variant="p" className="text-gray-700">
          {getCorrectAnswerExplanation()}
        </Typography>
      </Modal>
    </div>
  );
};

export default DataVisualizationPreview;
